import type React from "react";
import { useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import type { appUser, reportingPeriod } from "@duress/shared";
import { formatDateRange } from "@/lib/format";
import { useClientBySlug } from "../clients/useClients";
import { useAppUsers } from "../clients/useAppUsers";
import {
  usePeriods,
  useDashboardView,
  useMonthlyTrends,
  type SelectedView,
  type TrendPoint,
} from "../periods/usePeriods";
import { decodeViewParams } from "./viewParams";
import { PresentShell } from "./PresentShell";
import { useSlideDeck } from "./useSlideDeck";
import type { Slide } from "./slide.types";
import { overviewSlide } from "./slides/OverviewSlide";
import { teamPerformanceSlide } from "./slides/TeamPerformanceSlide";
import { inactiveTeamsSlide } from "./slides/InactiveTeamsSlide";
import { deviceMixSlide } from "./slides/DeviceMixSlide";
import { emergencySlide } from "./slides/EmergencySlide";
import { checkinSlide } from "./slides/CheckinSlide";
import { teamAlertSlide } from "./slides/TeamAlertSlide";
import { topUsersSlide } from "./slides/TopUsersSlide";
import { appUsageSlide } from "./slides/AppUsageSlide";
import { monthlyTrendsSlide } from "./slides/MonthlyTrendsSlide";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function formatPeriodLabel(p: reportingPeriod.ReportingPeriod): string {
  return `${MONTH_NAMES[p.month - 1] ?? String(p.month)} ${String(p.year)}`;
}

// App-Only clients have no device/session CSV, so the four device-derived
// slides (Overview, Team Performance, Inactive Teams, Device Mix) and Top
// Users/Monthly Trends -- all sourced from `methodology`/`trends`, see
// ClientDetailPage's getVisibleUsageTabs for the equivalent dashboard-side
// swap -- would only ever show empty/zeroed tiles. App Usage replaces them
// with the data App-Only clients actually have: registration and adoption
// numbers from the App User roster. A mixed client (devices + app users)
// keeps the device slides and gets App Usage appended, same as the
// dashboard's "app-usage" tab.
function buildSlides(
  data: reportingPeriod.DashboardData,
  trends: TrendPoint[],
  appOnly: boolean | null,
  appUserData: appUser.AppUserData | null,
): Slide[] {
  const list: Slide[] = [];
  if (!appOnly) {
    list.push(overviewSlide, teamPerformanceSlide, inactiveTeamsSlide, deviceMixSlide);
    if (data.methodology.users.length > 0) list.push(topUsersSlide);
  }
  if (appUserData !== null) list.push(appUsageSlide);
  if (data.emergency !== null) list.push(emergencySlide);
  if (data.checkin !== null) list.push(checkinSlide);
  if (data.team_alert !== null) list.push(teamAlertSlide);
  if (!appOnly && trends.length > 1) list.push(monthlyTrendsSlide);
  return list;
}

function FullscreenStatus({
  message,
  onExit,
}: {
  message: string;
  onExit?: () => void;
}): React.JSX.Element {
  return (
    <div className="fixed inset-0 bg-black text-white flex flex-col items-center justify-center gap-4">
      <p className="text-sm text-white/70">{message}</p>
      {onExit ? (
        <button
          type="button"
          onClick={onExit}
          className="text-sm underline text-white/80 hover:text-white"
        >
          Back to dashboard
        </button>
      ) : null}
    </div>
  );
}

export function PresentPage(): React.JSX.Element {
  const { slug = "" } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: client, isLoading: clientLoading, error: clientError } = useClientBySlug(slug);
  const { data: periodsData, isLoading: periodsLoading } = usePeriods(client?.id ?? "");
  const periods = periodsData?.items ?? [];
  const { trends, isLoading: trendsLoading } = useMonthlyTrends(client?.id ?? "");

  // The client page encodes its currently-selected view into the URL query
  // string when it opens Present mode in a new tab, so Present shows the same
  // period/range instead of always the latest. Falls back to the latest
  // period when opened without a view (e.g. a bare bookmark) or when the
  // requested period no longer exists.
  const requestedView = decodeViewParams(searchParams);

  const view = useMemo<SelectedView | null>(() => {
    if (
      requestedView &&
      (requestedView.mode !== "period" || periods.some((p) => p.id === requestedView.periodId))
    ) {
      return requestedView;
    }
    const id = periods[0]?.id;
    return id ? { mode: "period", periodId: id } : null;
  }, [requestedView, periods]);

  const {
    data: dashboardData,
    isLoading: dashboardLoading,
    error: dashboardError,
  } = useDashboardView(client?.id ?? "", view);

  const shouldLoadAppUsers = client?.appOnly === true || client?.hasAppUsers === true;
  const { data: appUserData, isLoading: appUsersLoading } = useAppUsers(client?.id ?? "", {
    enabled: shouldLoadAppUsers,
  });

  const slides = useMemo<Slide[]>(
    () =>
      dashboardData
        ? buildSlides(dashboardData, trends, client?.appOnly ?? null, appUserData ?? null)
        : [],
    [dashboardData, trends, client?.appOnly, appUserData],
  );
  const deck = useSlideDeck(slides);

  const handleExit = (): void => {
    navigate(`/clients/${slug}`);
  };

  if (
    clientLoading ||
    periodsLoading ||
    dashboardLoading ||
    trendsLoading ||
    (shouldLoadAppUsers && appUsersLoading)
  ) {
    return <FullscreenStatus message="Loading…" />;
  }

  if (clientError || !client) {
    return <FullscreenStatus message="Could not load client." onExit={handleExit} />;
  }

  if (periods.length === 0) {
    return <FullscreenStatus message="No reporting periods uploaded yet." onExit={handleExit} />;
  }

  if (dashboardError || !dashboardData) {
    return (
      <FullscreenStatus
        message="Could not load reporting data for the selected view."
        onExit={handleExit}
      />
    );
  }

  const periodLabel = ((): string => {
    if (!view || view.mode === "period") {
      const p = periods.find((period) => period.id === view?.periodId) ?? periods[0];
      return p ? formatPeriodLabel(p) : "";
    }
    if (view.mode === "range") return formatDateRange(view.from, view.to);
    return "All time";
  })();

  return (
    <PresentShell
      deck={deck}
      context={{
        clientSlug: client.slug,
        clientName: client.name,
        periodLabel,
        data: dashboardData,
        trends,
        appUserData: appUserData ?? null,
      }}
      onExit={handleExit}
    />
  );
}
